import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  FaPlus,
  FaVolumeUp,
  FaDownload,
  FaTimes,
  FaEdit,
  FaTrashAlt
} from "react-icons/fa";
import { NODE_HOST_URL, NODE_HOST_URL_v1 } from "../api/Api";
import DashboardHeader from "../../Header/DashboardHeader";
import DashboardFooter from "../../Footer/DashboardFooter";
import DashboardLeftMenu from "../../Dashboard/DashboardLeftMenu";
import AdvanceDataTable from "../../UI/Table/AdvanceDataTable";
import styles from "./TTSCreateGroup.module.css";
import { handleUserLog } from "../../Utils/Utils";


// ✅ Updated column keys to match API response

const TTSCreateGroup = () => {

  const GROUP_LIST_COLUMNS = [
    {
      name: "ID",
      selector: (row) => row.id,
      sortable: true,
    },
    {
      name: "Group Name",
      selector: (row) => row.name,
      sortable: true,
    },
    {
      name: "Description",
      selector: (row) => row.description,
      sortable: true,
    },
    {
      name: "Created Date",
      selector: (row) => row.created_date,
      sortable: true,
    },
    {
      name: "Clips",
      selector: (row) => row.clip_count ?? 0,
      sortable: true,
    },

    {
      name: "Actions",
      cell: (row) => (
        <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
          <FaEdit
            style={{ cursor: "pointer", color: "#007bff" }}
            title="Edit"
            onClick={() => {
              handleEdit(row);
            }}
          />
          <FaTrashAlt
            style={{ cursor: "pointer", color: "#dc3545" }}
            title="Delete"
            onClick={() => {
              handleDelete(row);
            }}
          />
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
  ];

  const [groupName, setGroupName] = useState("");
  const [groupId, setGroupId] = useState("");
  const [description, setDescription] = useState("");
  const [groups, setGroups] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [editGroupName, setEditGroupName] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState(null);



  const loginData = JSON.parse(localStorage.getItem("login"));
  const user_id = loginData?.user?.userId;
  const user_name = loginData?.user?.name;
  const token = loginData?.token;

  useEffect(() => {
    if (user_id && token && user_name) {
      fetchGroups();
    }
  }, []);

  const handleEdit = (row) => {
    setSelectedGroup(row);
    setEditGroupName(row.name);
    setEditDescription(row.description);
    setShowEditModal(true);
  };

  const handleDelete = (row) => {
    setGroupToDelete(row);
    setShowDeleteModal(true);
  };

  const confirmDeleteGroup = async () => {
    if (!groupToDelete) return;

    try {
      const res = await axios.post(`${NODE_HOST_URL_v1}/clipGroup`, {
        user_id,
        token,
        method: "delete",
        group_id: groupToDelete.id,
        groupName: groupToDelete.name,
      });

      if (res.data?.success) {
        setSuccessMessage("Group deleted successfully");
        setTimeout(() => setSuccessMessage(""), 3000);
        handleUserLog("", "delete_group", "voice", "Group deleted successfully", "tts_group_list", "", { group_id: groupToDelete.id, group_name: groupToDelete.name });
        fetchGroups();
      } else {
        setErrorMessage(res.data?.message || "Delete failed");
        setTimeout(() => setErrorMessage(""), 3000);
        handleUserLog("", "delete_group", "voice", res.data?.message || "Delete failed", "tts_group_list", "", { group_id: groupToDelete.id, group_name: groupToDelete.name });
      }
    } catch (err) {
      console.error("Delete error:", err);
      setErrorMessage("Something went wrong.");
      setTimeout(() => setErrorMessage(""), 3000);
      handleUserLog("", "delete_group", "voice", "Something went wrong.", "tts_group_list", "", { group_id: groupToDelete.id, group_name: groupToDelete.name });
    }

    setShowDeleteModal(false);
    setGroupToDelete(null);
  };


  const handleEditSubmit = async () => {
    try {
      const res = await axios.post(`${NODE_HOST_URL_v1}/clipGroup`, {
        user_id,
        token,
        method: "editGroup",
        oldGroupName: selectedGroup.name,
        newGroupName: editGroupName,
        group_id: selectedGroup.id,
        description: editDescription,
      });

      if (res.data?.success) {
        setSuccessMessage("Group updated successfully");
        setShowEditModal(false);
        handleUserLog("", "updated_tts_group", "voice", "Group updated successfully", "tts_group_list", "", { group_id: selectedGroup.id, group_name: editGroupName });
        fetchGroups();
      } else {
        setErrorMessage(res.data?.message || "Update failed");
        handleUserLog("", "updated_tts_group", "voice", res.data?.message || "Update failed", "tts_group_list", "", { group_id: selectedGroup.id, group_name: editGroupName });
      }
    } catch (err) {
      console.error("Edit error:", err);
      setErrorMessage("Something went wrong.");
      handleUserLog("", "updated_tts_group", "voice", "Something went wrong.", "tts_group_list", "", { group_id: selectedGroup.id, group_name: editGroupName });
    }
  };


  const fetchGroups = async () => {
    try {
      const res = await axios.post(`${NODE_HOST_URL_v1}/clipGroup`, {
        user_id,
        token,
        method: "fetch_ttsclip_group",
      });

      console.log("Fetched groups:", res.data);

      if (res.data?.success && Array.isArray(res.data.data)) {
        const groupList = res.data.data;

       
        const groupsWithClipCount = await Promise.all(
          groupList.map(async (group) => {
            try {
              const clipRes = await axios.post(`${NODE_HOST_URL_v1}/clipGroup`, {
                user_id,
                token,
                method: "fetch_tts_clip",
                clip_groupid: group.id, 
              });

              const clipCount = Array.isArray(clipRes.data?.data)
                ? clipRes.data.data.length
                : 0;

              return {
                ...group,
                clip_count: clipCount,
              };
            } catch (clipErr) {
              console.error(`Failed to fetch clips for group ID ${group.id}`, clipErr);
              return {
                ...group,
                clip_count: 0,
              };
            }
          })
        );

        setGroups(groupsWithClipCount);
      } else {
        console.warn("No groups found or response format incorrect");
        setGroups([]);
      }
    } catch (err) {
      console.error("Error fetching groups:", err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!groupName) return;

    try {
      const res = await axios.post(`${NODE_HOST_URL_v1}/clipGroup`, {
        user_id,
        token,
        user_name,
        groupName,
        description,
        status: 1,
        method: "createGroup",
      });

      console.log("Group creation response:", res.data);

      if (!res.data?.success) {
        setErrorMessage(res.data.message || "Something went wrong.");
        setTimeout(() => setErrorMessage(""), 3000);
        handleUserLog("", "create_group", "voice", res.data.message || "Something went wrong.", "tts_group_list", "", { group_name: groupName, description });
        return;
      }

      setSuccessMessage(res.data.message || "Group created successfully!");
      setGroupName("");
      setDescription("");
      setTimeout(() => setSuccessMessage(""), 3000);
      setTimeout(() => fetchGroups(), 1000);
      handleUserLog("", "create_group", "voice", res.data.message || "Group created successfully!", "tts_group_list", "", { group_name: groupName, description });
    } catch (err) {
      console.error("Error creating group:", err);
      setErrorMessage("Something went wrong. Please try again.");
      setTimeout(() => setErrorMessage(""), 3000);
      handleUserLog("", "create_group", "voice", "Something went wrong. Please try again.", "tts_group_list", "", { group_name: groupName, description });
    }
  };

  if (!user_id || !token || !user_name) {
    return (
      <div className="text-center mt-5">
        <h4>Please log in to access this page.</h4>
      </div>
    );
  }

  return (
    <div className="wrapper">
      <DashboardLeftMenu />
      <div className="main">
        <DashboardHeader />
        {showEditModal && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              background: "rgba(0,0,0,0.5)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 1000,
            }}
          >
            <div
              style={{
                background: "#fff",
                padding: "20px",
                borderRadius: "8px",
                width: "400px",
                boxShadow: "0 0 10px rgba(0,0,0,0.3)",
              }}
            >
              <h5>Edit Group</h5>
              <div className="mb-3">
                <label>Group Name</label>
                <input
                  className="form-control"
                  value={editGroupName}
                  onChange={(e) => setEditGroupName(e.target.value)}
                />
              </div>
              {/* <div className="mb-3">
                <label>Description</label>
                <textarea
                  className="form-control"
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                />
              </div> */}
              <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
                <button className={styles.saveButton} onClick={handleEditSubmit}>
                  Save
                </button>
                <button className="btn btn-secondary" onClick={() => setShowEditModal(false)}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {showDeleteModal && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              background: "rgba(0,0,0,0.5)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 1000,
            }}
          >
            <div
              style={{
                background: "#fff",
                padding: "20px",
                borderRadius: "8px",
                width: "400px",
                boxShadow: "0 0 10px rgba(0,0,0,0.3)",
              }}
            >
              <h5>Confirm Delete</h5>
              <p>Are you sure you want to delete the group <strong>{groupToDelete?.name}</strong>?</p>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
                <button className="btn btn-danger" onClick={confirmDeleteGroup}>
                  Delete
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowDeleteModal(false);
                    setGroupToDelete(null);
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}


        <div style={{ padding: "2rem" }}>
          <div style={{ background: "#fff", padding: "2rem", borderRadius: "12px", marginBottom: "2rem" }}>
            <h4>
              <FaPlus style={{ marginRight: "8px" }} />
              Create Group
            </h4>

            {/* {errorMessage && (
              <div className="alert alert-danger mt-3">{errorMessage}</div>
            )}
            {successMessage && (
              <div className="alert alert-success mt-3">{successMessage}</div>
            )} */}

              {successMessage && (
          <div className="alert alert-success d-flex justify-content-between align-items-center">
            <span>{successMessage}</span>
            <button onClick={() => setSuccessMessage("")} style={{ background: "none", border: "none", fontSize: "20px" }}>
              &times;
            </button>
          </div>
        )}
        {errorMessage && (
          <div className="alert alert-danger d-flex justify-content-between align-items-center">
            <span>{errorMessage}</span>
            <button onClick={() => setErrorMessage("")} style={{ background: "none", border: "none", fontSize: "20px" }}>
              &times;
            </button>
          </div>
        )}

            <form onSubmit={handleSubmit} style={{ marginTop: "1.5rem" }}>
              <div className="mb-3">
                <label className="form-label">Group Name *</label>
                <input
                  className="form-control"
                  placeholder="e.g. Promotions Group"
                  value={groupName}
                  onChange={(e) => setGroupName(e.target.value)}
                  required
                />
              </div>

              <div className="mb-3">
                <label className="form-label">Group Description</label>
                <textarea
                  className="form-control"
                  placeholder="Optional: Describe your clip group..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>

              <button type="submit" className={styles.saveButton}>
                Save Group
              </button>

            </form>
          </div>

          {/* ✅ Default AdvanceDataTable styling, no custom CSS */}
          <div>
            <h5>Audio Groups</h5>
            <AdvanceDataTable
              tableCell={GROUP_LIST_COLUMNS}
              tableData={groups}
              isLoading={false}
            />
          </div>
        </div>

        <DashboardFooter />
      </div>
    </div>
  );
};

export default TTSCreateGroup;
